import { configure as configureAnalytics, tracker } from '@ixigo/analytics';
import { configure } from '@ixigo/shell';
import { patchHistory } from '@repo/shared/navigation';
import { StartClient } from '@tanstack/react-start/client';
import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { cookieStore } from '@/utils/cookie';
import {
	getCommonHeaders,
	getRequestContext,
	ixiUIDResolver,
} from '@/utils/headers';
import { browserAuth } from './lib/login/auth';
import { getAppContext } from './utils/app-context';
import { initGlobalAppTracker } from './utils/track';

async function initEnvironment() {
	patchHistory();
	const getIxiUID = ixiUIDResolver(cookieStore);

	// fire early to set the ixiUID cookie
	getIxiUID();

	const appContext = getAppContext();

	/**
	 * makes the fetch client available to the entire application
	 * with the given configuration
	 */
	configure({
		prefixUrl: import.meta.env.BASE_API_URL,
		hooks: {
			beforeRequest: async (request) => {
				await getIxiUID();
				// Auth header is owned by browserAuth.interceptor below, not set here
				// (getCommonHeaders no longer emits Authorization at all).
				const ctx = getCommonHeaders(
					getRequestContext({
						getCookie: (key) => cookieStore.getCookie(key) || '',
						ua: navigator.userAgent,
					}),
				);
				if (ctx) {
					Object.entries(ctx).forEach(([k, v]) => request.headers.set(k, v));
				}
				// The login SDK owns the auth header (and skips auth-internal requests).
				browserAuth.interceptor.beforeRequest(request);
			},
			beforeError: async (httpError) => {
				// SDK clears its own session + emits auth-changed on a genuine 401;
				// no cookie-name knowledge or page reload here.
				browserAuth.interceptor.beforeError(httpError.response);
				return httpError;
			},
		},
	});

	configureAnalytics({
		clevertapProjectId: 'R5Z-849-WZ4Z',
		clientId: appContext.clientConfig().clientId,
		// TODO: fix this type issue in "@ixigo/analytics"
		cookie: cookieStore as Parameters<typeof configureAnalytics>[0]['cookie'],
	});
	tracker.onEvent(({ data, name }) => {
		if (import.meta.env.PUBLIC_APP_ENV !== 'prod') {
			// Group related console logs
			console.group(
				`%c🔍 Analytics Event: ${name}`,
				'color: #0770E4; font-weight: bold;',
			);
			// Log event properties in a table if they exist
			if (data) {
				console.table(data);
			}
			console.groupEnd();
		}
	});
	initGlobalAppTracker();
}

initEnvironment().then(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<StartClient />
		</StrictMode>,
	);

	// bfcache can restore the page with stale visual state — e.g. Chrome's
	// own back-forward visual transitions may leave <html> visibility:hidden,
	// or app-level exit animations (page-exit class) may still be applied.
	// Force-clear these so the restored page is always visible.
	window.addEventListener('pageshow', (event) => {
		if (!event.persisted) return;
		document.documentElement.style.visibility = '';
		document.body.classList.remove('page-exit');
		document.getElementById('gateway-form')?.remove();
	});
});
